import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Card } from '../atoms/Card/Card';
import { Checkbox } from '../atoms/Checkbox/Checkbox';
import { EmptyState } from '../atoms/EmptyState';
import { Grid } from '../atoms/Grid/Grid';
import { Icon } from '../atoms/Icon';
import { Inline } from '../atoms/Inline/Inline';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Alert } from '../atoms/Alert/Alert';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { useToast } from '../../stores/toastStore';
import { ApiError } from '../../lib/apiClient';
import { formatMeasuredAt } from '../../utils/measurementDate';
import {
  HISTORY_RANGES,
  MAX_HISTORY_PARAMETERS,
  deleteMeasurement,
  getMeasurementHistory,
  updateMeasurement,
  type HistoryEntry,
  type HistoryParameter,
  type HistoryRange,
  type HistorySource,
  type MeasurementUpdate,
  type ParameterTarget,
} from '../../services/measurementService';
import { MeasurementTrendChart, type ProbeLine } from './MeasurementTrendChart';
import { useProbeHistory } from '../../hooks/useController';
import { MeasurementCombinedChart } from './MeasurementCombinedChart';
import { MeasurementHistoryTable } from './MeasurementHistoryTable';
import { EvidencePhotoDialog } from './EvidencePhotoDialog';
import { EditMeasurementDialog } from './EditMeasurementDialog';
import { DeleteMeasurementDialog } from './DeleteMeasurementDialog';
import { ParameterSwatch } from './ParameterSwatch';
import { assignSeriesColours } from './parameterColours';
import { sharedTimeline, type ChartSeries } from './historyChartUtils';
import { useHistoryUrlState } from './useHistoryUrlState';
import { formatNumber, groupByParameter, parameterLabel, summarizeSeries } from './measurementHistoryUtils';
import { interventionsKey, listInterventions } from '../../services/incidentService';
import './MeasurementHistory.css';

const SOURCES: readonly HistorySource[] = ['manual', 'photo', 'icp', 'fauna_marin', 'controller', 'device'];
type SourceFilter = 'all' | HistorySource;
/** Charts of one view share their tooltip / crosshair (Recharts syncId). */
export const HISTORY_SYNC_ID = 'measurement-history';

export interface MeasurementHistoryProps {
  aquariumId: string;
  /** Switches to the entry view (empty-state action). */
  onAddMeasurement: () => void;
}

/** Parameters of the range from the API, or — for older responses — derived from the values. */
function availableParameters(parameters: HistoryParameter[] | undefined, entries: readonly HistoryEntry[]): HistoryParameter[] {
  if (parameters) return parameters;
  return groupByParameter(entries).map((s) => ({
    parameterId: s.parameterId,
    parameterName: s.parameterName,
    unit: s.unit,
    count: s.points.length,
  }));
}

/**
 * "Verlauf" (audit #14, #1653): compare 1–3 parameters over a selectable range
 * — one chart per parameter with its own unit, target band and colour on a
 * shared, synchronised time axis, or all of them as % of their target range
 * ("Gemeinsam darstellen") — with a text summary per parameter and the full
 * value table (manual, photo, ICP, Fauna Marin) incl. evidence photo viewer
 * (#15), correction and deletion. The selection lives in the URL.
 */
export function MeasurementHistory({ aquariumId, onAddMeasurement }: MeasurementHistoryProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { urlParameters, combined: combinedRequested, setParameters, setCombined, clearParameters } = useHistoryUrlState();

  const [range, setRange] = useState<HistoryRange>('90d');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [evidenceEntry, setEvidenceEntry] = useState<HistoryEntry | null>(null);
  const [editEntry, setEditEntry] = useState<HistoryEntry | null>(null);
  const [deleteEntry, setDeleteEntry] = useState<HistoryEntry | null>(null);

  const historyQuery = useQuery({
    queryKey: ['measurements', aquariumId, 'history', range, urlParameters?.join(',') ?? ''],
    queryFn: () => getMeasurementHistory(aquariumId, range, urlParameters),
    // Keep the charts on screen while another selection loads.
    placeholderData: keepPreviousData,
  });

  // A shared / edited link with a parameter the aquarium does not have → the default selection.
  const unknownParameter =
    urlParameters !== null && historyQuery.error instanceof ApiError && historyQuery.error.code === 'UNKNOWN_PARAMETER';
  useEffect(() => {
    if (unknownParameter) clearParameters();
  }, [unknownParameter, clearParameters]);

  const updateMutation = useMutation({
    mutationFn: ({ entry, update }: { entry: HistoryEntry; update: MeasurementUpdate }) =>
      updateMeasurement(aquariumId, entry.id, update),
    onSuccess: () => {
      setEditEntry(null);
      void queryClient.invalidateQueries({ queryKey: ['measurements', aquariumId] });
      showToast(t('measurement.history.edit.saved'), 'success');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (entry: HistoryEntry) => deleteMeasurement(aquariumId, entry.id),
    onSuccess: () => {
      setDeleteEntry(null);
      void queryClient.invalidateQueries({ queryKey: ['measurements', aquariumId] });
      showToast(t('measurement.history.delete.deleted'), 'success');
    },
  });

  const data = historyQuery.data;
  const allEntries = useMemo(() => data?.entries ?? [], [data]);
  const targets: Record<string, ParameterTarget> = useMemo(() => data?.targets ?? {}, [data]);
  const available = useMemo(() => availableParameters(data?.parameters, allEntries), [data?.parameters, allEntries]);

  // URL selection, else the most-tested parameter of the range (the #1641 default).
  const selectedIds = useMemo(
    () => urlParameters ?? (available[0] ? [available[0].parameterId] : []),
    [urlParameters, available],
  );
  const colourSlots = useMemo(() => assignSeriesColours(selectedIds), [selectedIds]);

  const selectedEntries = useMemo(() => {
    const ids = new Set(selectedIds);
    return allEntries.filter((e) => ids.has(e.parameterId));
  }, [allEntries, selectedIds]);
  const visibleEntries = useMemo(
    () =>
      (sourceFilter === 'all' ? selectedEntries : selectedEntries.filter((e) => e.source === sourceFilter))
        .slice()
        .sort((a, b) => Date.parse(b.measuredAt) - Date.parse(a.measuredAt)),
    [selectedEntries, sourceFilter],
  );

  const nameOf = useCallback(
    (id: string): string => {
      const known = available.find((p) => p.parameterId === id) ?? allEntries.find((e) => e.parameterId === id);
      return parameterLabel(t, id, known?.parameterName ?? id);
    },
    [available, allEntries, t],
  );

  // Memoised so the charts only redraw when the data or the selection changes.
  const series: ChartSeries[] = useMemo(
    () =>
      selectedIds.map((id) => {
        const points = visibleEntries.filter((e) => e.parameterId === id).reverse(); // oldest first
        const meta = available.find((p) => p.parameterId === id);
        return {
          parameterId: id,
          label: nameOf(id),
          unit: points[points.length - 1]?.unit ?? meta?.unit ?? '',
          points,
          target: targets[id],
          slot: colourSlots.get(id) ?? 'kh',
        };
      }),
    [selectedIds, visibleEntries, available, nameOf, targets, colourSlots],
  );
  const timeline = useMemo(() => sharedTimeline(series), [series]);
  // Controller probes (#1899): a dashed line behind the test values — only in the unit of the tests,
  // a probe in another unit (e.g. conductivity for salinity) would need its own axis.
  const probeHistory = useProbeHistory(aquariumId, range, selectedIds);
  const probes = useMemo(() => {
    const out = new Map<string, ProbeLine>();
    for (const s of series) {
      const p = probeHistory.data?.[s.parameterId];
      if (!p || p.readings.length === 0) continue;
      const unit = (p.unit ?? '').trim();
      if (s.points.length > 0 && unit.toLowerCase() !== s.unit.trim().toLowerCase()) continue;
      out.set(s.parameterId, {
        label: p.origin ?? p.label ?? String(t('controller.probe.controller')),
        unit: unit || s.unit,
        points: p.readings.map((r) => ({ time: Date.parse(r.measuredAt), value: r.value, min: r.min, max: r.max })),
      });
    }
    return out;
  }, [series, probeHistory.data, t]);
  // One x range for every chart: the tests and the probe readings.
  const chartDomain = useMemo((): [number, number] | undefined => {
    const times = timeline.map((p) => p.time);
    for (const probe of probes.values()) for (const p of probe.points) times.push(p.time);
    return times.length >= 2 ? [Math.min(...times), Math.max(...times)] : undefined;
  }, [timeline, probes]);
  // Reef Guard #1824: logged interventions as markers on the charts (and as text below them).
  const rangeFrom = timeline[0]?.time ?? null;
  const rangeTo = timeline[timeline.length - 1]?.time ?? null;
  const interventionsQuery = useQuery({
    queryKey: [...interventionsKey(aquariumId), rangeFrom, rangeTo],
    queryFn: () =>
      listInterventions(aquariumId, { from: new Date(rangeFrom as number).toISOString(), to: new Date(rangeTo as number).toISOString() }),
    enabled: rangeFrom !== null && rangeTo !== null && rangeTo > rangeFrom,
    retry: false,
  });
  const interventions = useMemo(
    () => [...(interventionsQuery.data ?? [])].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt)),
    [interventionsQuery.data],
  );
  const markers = useMemo(() => interventions.map((i) => ({ time: Date.parse(i.occurredAt), key: i.id })), [interventions]);

  const missingTargets = series.filter((s) => !s.target);
  const canCombine = series.length >= 2 && missingTargets.length === 0;
  const combinedActive = combinedRequested && canCombine;
  const combineHint =
    series.length < 2
      ? t('measurement.history.combined.needTwo')
      : missingTargets.length > 0
        ? t('measurement.history.combined.needTargets', { parameters: missingTargets.map((s) => s.label).join(', ') })
        : null;

  const rangeOptions = HISTORY_RANGES.map((value) => ({ value, label: t(`measurement.history.range.${value}`) }));
  const parameterOptions = [
    ...available.map((p) => ({ value: p.parameterId, label: `${nameOf(p.parameterId)} (${p.count})` })),
    // A selected parameter without values in this range stays listed, so it can be removed.
    ...selectedIds
      .filter((id) => !available.some((p) => p.parameterId === id))
      .map((id) => ({ value: id, label: `${nameOf(id)} (0)` })),
  ];
  const limitReached = selectedIds.length >= MAX_HISTORY_PARAMETERS;

  const openEdit = (entry: HistoryEntry): void => {
    updateMutation.reset();
    setEditEntry(entry);
  };
  const openDelete = (entry: HistoryEntry): void => {
    deleteMutation.reset();
    setDeleteEntry(entry);
  };

  const hasData = available.length > 0;
  const controls = (
    <Stack gap="3" className="measurement-history__controls">
      <Grid minItemWidth="sm" gap="4" className="measurement-history__filters">
        <Select
          label={t('measurement.history.rangeLabel')}
          options={rangeOptions}
          value={range}
          onChange={(e) => setRange(e.target.value as HistoryRange)}
          fullWidth
        />
        {hasData && (
          <Select
            label={t('measurement.history.filterLabel')}
            options={[
              { value: 'all', label: t('measurement.history.filterAll') },
              ...SOURCES.map((source) => ({ value: source, label: t(`measurement.history.source.${source}`) })),
            ]}
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
            fullWidth
          />
        )}
        {hasData && (
          <Select
            variant="multiple"
            id="measurement-history-parameters"
            label={t('measurement.history.parametersLabel')}
            options={parameterOptions}
            values={selectedIds}
            onValuesChange={setParameters}
            maxSelected={MAX_HISTORY_PARAMETERS}
            minSelected={1}
            hint={
              limitReached
                ? t('measurement.history.filter.maxHint', { max: MAX_HISTORY_PARAMETERS })
                : t('measurement.history.filter.hint', { max: MAX_HISTORY_PARAMETERS })
            }
            placeholder={t('measurement.history.filter.placeholder')}
            fullWidth
          />
        )}
      </Grid>
      {hasData && (
        <div className="measurement-history__combine">
          <Checkbox
            id="measurement-history-combined"
            label={t('measurement.history.combined.toggle')}
            checked={combinedActive}
            disabled={!canCombine}
            aria-describedby={combineHint ? 'measurement-history-combined-hint' : undefined}
            onChange={(e) => setCombined(e.target.checked)}
          />
          {combineHint && (
            <Text id="measurement-history-combined-hint" size="sm" color="secondary">
              {combineHint}
            </Text>
          )}
        </div>
      )}
    </Stack>
  );

  const probeSummary = (s: ChartSeries): ReactNode => {
    const probe = probes.get(s.parameterId);
    const last = probe?.points[probe.points.length - 1];
    if (!probe || !last) return null;
    const values = probe.points.map((p) => p.value);
    return (
      <span data-testid={`history-probe-summary-${s.parameterId}`}>
        {' '}
        {t('controller.probe.chartSummary', {
          origin: probe.label,
          latest: `${formatNumber(last.value, language)} ${probe.unit}`.trim(),
          min: formatNumber(Math.min(...values), language),
          max: formatNumber(Math.max(...values), language),
        })}
      </span>
    );
  };

  const summaryText = (s: ChartSeries): ReactNode => {
    const summary = summarizeSeries(s.points, s.target);
    if (!summary.latest) {
      return t('measurement.history.summary.none', { parameter: s.label });
    }
    return (
      <>
        {t('measurement.history.summary.main', {
          count: summary.count,
          parameter: s.label,
          latest: `${formatNumber(summary.latest.value, language)} ${s.unit}`.trim(),
          date: formatMeasuredAt(summary.latest, language),
          min: formatNumber(summary.min ?? 0, language),
          max: formatNumber(summary.max ?? 0, language),
        })}{' '}
        {s.target && summary.inTarget !== null
          ? t('measurement.history.summary.target', {
              inTarget: summary.inTarget,
              count: summary.count,
              min: formatNumber(s.target.min, language),
              max: formatNumber(s.target.max, language),
              unit: s.unit,
            })
          : t('measurement.history.summary.noTarget')}
      </>
    );
  };

  const isRefetchingSelection = historyQuery.isPlaceholderData;

  let body;
  if (historyQuery.isPending || unknownParameter) {
    body = (
      <Card>
        <LiveRegion busy>{t('measurement.history.loading')}</LiveRegion>
      </Card>
    );
  } else if (historyQuery.isError) {
    body = (
      <LoadErrorState
        title={t('measurement.history.loadError')}
        onRetry={() => void historyQuery.refetch()}
        retryLabel={t('measurement.history.retry')}
        retrying={historyQuery.isFetching}
        titleLevel={2}
      />
    );
  } else if (!hasData) {
    body = (
      <Card>
        {range === 'all' ? (
          <EmptyState
            icon={<Icon name="droplet" />}
            headline={t('measurement.history.empty.title')}
            description={t('measurement.history.empty.description')}
            actionLabel={t('measurement.history.empty.action')}
            onAction={onAddMeasurement}
          />
        ) : (
          <EmptyState
            icon={<Icon name="search-off" />}
            headline={t('measurement.history.emptyRange.title')}
            description={t('measurement.history.emptyRange.description')}
            actionLabel={t('measurement.history.emptyRange.action')}
            onAction={() => setRange('all')}
          />
        )}
      </Card>
    );
  } else if (visibleEntries.length === 0 && selectedEntries.length > 0) {
    body = (
      <Card>
        <EmptyState
          icon={<Icon name="search-off" />}
          headline={t('measurement.history.emptySource.title')}
          description={t('measurement.history.emptySource.description')}
          actionLabel={t('measurement.history.emptySource.action')}
          onAction={() => setSourceFilter('all')}
        />
      </Card>
    );
  } else {
    const charts = combinedActive ? (
      <Card>
        <Stack gap="4">
          <Heading level={2}>{t('measurement.history.combined.title')}</Heading>
          <ul className="measurement-history__legend" aria-label={t('measurement.history.combined.legend')}>
            {series.map((s) => (
              <li key={s.parameterId} className="measurement-history__legend-item">
                <ParameterSwatch slot={s.slot} />
                <Text as="span" size="sm">
                  {t('measurement.history.combined.legendItem', {
                    parameter: s.label,
                    min: formatNumber(s.target?.min ?? 0, language),
                    max: formatNumber(s.target?.max ?? 0, language),
                    unit: s.unit,
                  })}
                </Text>
              </li>
            ))}
          </ul>
          <MeasurementCombinedChart series={series} caption={t('measurement.history.combined.description')} />
          <Heading level={3}>{t('measurement.history.summary.title')}</Heading>
          <ul className="measurement-history__summaries">
            {series.map((s) => (
              <li key={s.parameterId} data-testid={`history-summary-${s.parameterId}`}>
                <Text color="secondary">{summaryText(s)}</Text>
              </li>
            ))}
          </ul>
        </Stack>
      </Card>
    ) : (
      series.map((s) => (
        <Card key={s.parameterId} data-testid={`history-chart-card-${s.parameterId}`}>
          <Stack gap="4">
            <Inline gap="2" align="center" className="measurement-history__chart-title">
              <ParameterSwatch slot={s.slot} />
              <Heading level={2}>{t('measurement.history.chartTitle', { parameter: s.label })}</Heading>
            </Inline>
            {s.points.length >= 2 || (probes.get(s.parameterId)?.points.length ?? 0) >= 2 ? (
              <MeasurementTrendChart
                series={s}
                timeline={timeline}
                syncId={series.length > 1 ? HISTORY_SYNC_ID : undefined}
                markers={markers}
                probe={probes.get(s.parameterId)}
                domain={chartDomain}
                caption={
                  <>
                    <span data-testid={`history-summary-${s.parameterId}`}>{summaryText(s)}</span>
                    {probeSummary(s)}
                  </>
                }
              />
            ) : s.points.length === 1 ? (
              <Text color="secondary" data-testid={`history-summary-${s.parameterId}`}>
                {summaryText(s)} {t('measurement.history.singleValue')}
              </Text>
            ) : isRefetchingSelection ? (
              <Text color="secondary">{t('measurement.history.loading')}</Text>
            ) : (
              <Text color="secondary" data-testid={`history-summary-${s.parameterId}`}>
                {t('measurement.history.noValuesForParameter', { parameter: s.label })}
              </Text>
            )}
          </Stack>
        </Card>
      ))
    );

    const tableCaption =
      series.length === 1
        ? t('measurement.history.table.captionParameter', { parameter: series[0]!.label, count: visibleEntries.length })
        : t('measurement.history.table.captionParameters', {
            parameters: series.map((s) => s.label).join(', '),
            count: visibleEntries.length,
          });

    body = (
      <Stack gap="6">
        {data?.truncated && <Alert tone="info" live="off">{t('measurement.history.truncated')}</Alert>}
        <LiveRegion visuallyHidden busy={isRefetchingSelection}>
          {isRefetchingSelection
            ? t('measurement.history.loading')
            : t('measurement.history.selectionAnnounce', { parameters: series.map((s) => s.label).join(', ') })}
        </LiveRegion>
        {charts}
        {interventions.length > 0 && !combinedActive && (
          <Card data-testid="history-interventions">
            <Stack gap="2">
              <Heading level={2}>{t('incidents.history.title')}</Heading>
              <Text color="secondary">{t('incidents.history.description')}</Text>
              <ul className="measurement-history__interventions">
                {interventions.map((i) => (
                  <li key={i.id}>
                    <Text>
                      {t('incidents.history.item', {
                        date: formatMeasuredAt({ measuredAt: i.occurredAt }, language),
                        kind: t(`incidents.interventionKinds.${i.kind}`),
                      })}
                      {i.note ? ` — ${i.note}` : ''}
                    </Text>
                  </li>
                ))}
              </ul>
            </Stack>
          </Card>
        )}
        <Card>
          <Stack gap="4">
            <Heading level={2}>{t('measurement.history.tableTitle')}</Heading>
            <MeasurementHistoryTable
              entries={visibleEntries}
              targets={targets}
              caption={tableCaption}
              colourSlots={colourSlots}
              onViewEvidence={setEvidenceEntry}
              onEdit={openEdit}
              onDelete={openDelete}
            />
          </Stack>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap="6" className="measurement-history">
      {controls}
      {body}
      <EvidencePhotoDialog aquariumId={aquariumId} entry={evidenceEntry} onClose={() => setEvidenceEntry(null)} />
      <EditMeasurementDialog
        entry={editEntry}
        isSaving={updateMutation.isPending}
        saveFailed={updateMutation.isError}
        onSave={(entry, update) => updateMutation.mutate({ entry, update })}
        onClose={() => setEditEntry(null)}
      />
      <DeleteMeasurementDialog
        entry={deleteEntry}
        isDeleting={deleteMutation.isPending}
        deleteFailed={deleteMutation.isError}
        onConfirm={(entry) => deleteMutation.mutate(entry)}
        onClose={() => setDeleteEntry(null)}
      />
    </Stack>
  );
}

export default MeasurementHistory;
